/**
 * The forced choice, as pure state.
 *
 * Spec FR3: MOST and LEAST must both be set and must be different, and Next
 * stays disabled until they are. Rather than let the UI reach a state where the
 * same word is both and then refuse to advance, picking a word for one role
 * clears it from the other. The invalid state is unreachable, and `canAdvance`
 * is still checked before anything is sent.
 *
 * Kept free of React so the rule can be tested directly.
 */

import type { Slot } from '@dna17q/shared';

export type Role = 'most' | 'least';

export interface Selection {
  most: Slot | null;
  least: Slot | null;
}

export const EMPTY_SELECTION: Selection = { most: null, least: null };

/**
 * Applies a tap. Choosing a slot for one role removes it from the other, so
 * MOST and LEAST can never name the same word.
 */
export function select(current: Selection, role: Role, slot: Slot): Selection {
  if (role === 'most') {
    return { most: slot, least: current.least === slot ? null : current.least };
  }
  return { least: slot, most: current.most === slot ? null : current.most };
}

/** Spec FR3. Both set, and different. */
export function canAdvance(selection: Selection): selection is { most: Slot; least: Slot } {
  return selection.most !== null && selection.least !== null && selection.most !== selection.least;
}

/** Which role, if any, currently owns this slot. Drives the pressed state. */
export function roleOf(selection: Selection, slot: Slot): Role | null {
  if (selection.most === slot) return 'most';
  if (selection.least === slot) return 'least';
  return null;
}
